import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stack,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTheme } from "@mui/material/styles";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import {
  format,
  subDays,
  startOfDay,
  endOfDay,
  parseISO,
} from "date-fns";
import { ptBR } from "date-fns/locale";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { ColorModeContext } from "../../context/ColorMode/ColorModeContext";
import toastError from "../../errors/toastError";

const PERIODS = {
  today: 0,
  week: 7,
  month: 30,
  quarter: 90,
};

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  filterBar: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "space-between",
  },
  statCard: {
    padding: theme.spacing(2.5),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  statValue: {
    fontWeight: 700,
    fontSize: "2rem",
    lineHeight: 1.1,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  chartPaper: {
    padding: theme.spacing(2),
    height: 360,
    display: "flex",
    flexDirection: "column",
  },
  chartTitle: {
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
  },
  chartBody: {
    flex: 1,
    minHeight: 0,
  },
  loadingBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 320,
  },
}));

const toInputDate = date => format(date, "yyyy-MM-dd");

const formatDayLabel = day => {
  try {
    return format(parseISO(day), "dd/MM", { locale: ptBR });
  } catch {
    return day;
  }
};

const StatCard = ({ label, value, color }) => {
  const classes = useStyles();
  return (
    <Paper variant="outlined" className={classes.statCard}>
      <Typography className={classes.statLabel}>{label}</Typography>
      <Typography className={classes.statValue} style={{ color }}>
        {value}
      </Typography>
    </Paper>
  );
};

const DashboardLS = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { mode } = useContext(ColorModeContext);

  const [periodKey, setPeriodKey] = useState("month");
  const [dateFrom, setDateFrom] = useState(toInputDate(subDays(new Date(), 30)));
  const [dateTo, setDateTo] = useState(toInputDate(new Date()));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const chartTextColor = theme.palette.text.secondary;
  const gridColor =
    mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const tooltipStyle = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    color: theme.palette.text.primary,
  };

  const applyPreset = useCallback(key => {
    const today = new Date();
    const days = PERIODS[key] ?? 30;
    const from =
      key === "today"
        ? startOfDay(today)
        : startOfDay(subDays(today, days));
    setPeriodKey(key);
    setDateFrom(toInputDate(from));
    setDateTo(toInputDate(endOfDay(today)));
  }, []);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data: response } = await api.get("/barchart", {
        params: { dateFrom, dateTo },
      });
      setData(response);
    } catch (err) {
      toastError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const statusPieData = useMemo(() => {
    if (!data?.summary) return [];
    return [
      {
        name: i18n.t("dashboard.metrics.open"),
        value: data.summary.open,
        color: "#3b82f6",
      },
      {
        name: i18n.t("dashboard.metrics.pending"),
        value: data.summary.pending,
        color: "#990000",
      },
      {
        name: i18n.t("dashboard.metrics.closed"),
        value: data.summary.closed,
        color: "#22c55e",
      },
    ].filter(item => item.value > 0);
  }, [data]);

  const handlePeriodChange = (_, value) => {
    if (value) applyPreset(value);
  };

  const handleDateFromChange = e => {
    setPeriodKey("custom");
    setDateFrom(e.target.value);
  };

  const handleDateToChange = e => {
    setPeriodKey("custom");
    setDateTo(e.target.value);
  };

  return (
    <Container maxWidth="xl" className={classes.root}>
      <Typography variant="h4" gutterBottom>
        {i18n.t("dashboard.title")}
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        {i18n.t("dashboard.subtitle")}
      </Typography>

      <Paper variant="outlined" className={classes.filterBar}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={periodKey}
          onChange={handlePeriodChange}
        >
          <ToggleButton value="today">
            {i18n.t("dashboard.filters.today")}
          </ToggleButton>
          <ToggleButton value="week">
            {i18n.t("dashboard.filters.week")}
          </ToggleButton>
          <ToggleButton value="month">
            {i18n.t("dashboard.filters.month")}
          </ToggleButton>
          <ToggleButton value="quarter">
            {i18n.t("dashboard.filters.quarter")}
          </ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField
            label={i18n.t("dashboard.filters.from")}
            type="date"
            size="small"
            value={dateFrom}
            onChange={handleDateFromChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={i18n.t("dashboard.filters.to")}
            type="date"
            size="small"
            value={dateTo}
            onChange={handleDateToChange}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Paper>

      {loading ? (
        <Box className={classes.loadingBox}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label={i18n.t("dashboard.metrics.open")}
                value={data?.summary?.open ?? 0}
                color="#3b82f6"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label={i18n.t("dashboard.metrics.pending")}
                value={data?.summary?.pending ?? 0}
                color="#990000"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label={i18n.t("dashboard.metrics.closed")}
                value={data?.summary?.closed ?? 0}
                color="#22c55e"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label={i18n.t("dashboard.metrics.total")}
                value={data?.summary?.total ?? 0}
                color={theme.palette.primary.main}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" className={classes.chartPaper}>
                <Typography variant="subtitle1" className={classes.chartTitle}>
                  {i18n.t("dashboard.charts.ticketsByDay")}
                </Typography>
                <Box className={classes.chartBody}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data?.ticketsByDay ?? []}>
                      <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="day"
                        tickFormatter={formatDayLabel}
                        stroke={chartTextColor}
                        fontSize={12}
                      />
                      <YAxis
                        allowDecimals={false}
                        stroke={chartTextColor}
                        fontSize={12}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        labelFormatter={formatDayLabel}
                      />
                      <Line
                        type="monotone"
                        dataKey="total"
                        name={i18n.t("dashboard.metrics.tickets")}
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper variant="outlined" className={classes.chartPaper}>
                <Typography variant="subtitle1" className={classes.chartTitle}>
                  {i18n.t("dashboard.charts.statusDistribution")}
                </Typography>
                <Box className={classes.chartBody}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                      >
                        {statusPieData.map(entry => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" className={classes.chartPaper}>
                <Typography variant="subtitle1" className={classes.chartTitle}>
                  {i18n.t("dashboard.charts.topRequesters")}
                </Typography>
                <Box className={classes.chartBody}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={data?.topRequesters ?? []}
                      layout="vertical"
                      margin={{ left: 20 }}
                    >
                      <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                      <XAxis type="number" stroke={chartTextColor} fontSize={12} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={120}
                        stroke={chartTextColor}
                        fontSize={11}
                      />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar
                        dataKey="total"
                        name={i18n.t("dashboard.metrics.tickets")}
                        fill={theme.palette.primary.main}
                        radius={[0, 6, 6, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" className={classes.chartPaper}>
                <Typography variant="subtitle1" className={classes.chartTitle}>
                  {i18n.t("dashboard.charts.ticketsByUser")}
                </Typography>
                <Box className={classes.chartBody}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data?.ticketsByUser ?? []}>
                      <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        stroke={chartTextColor}
                        fontSize={11}
                        interval={0}
                        angle={-20}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis allowDecimals={false} stroke={chartTextColor} fontSize={12} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend />
                      <Bar
                        dataKey="open"
                        name={i18n.t("dashboard.metrics.open")}
                        fill="#3b82f6"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="closed"
                        name={i18n.t("dashboard.metrics.closed")}
                        fill="#22c55e"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" className={classes.chartPaper}>
                <Typography variant="subtitle1" className={classes.chartTitle}>
                  {i18n.t("dashboard.charts.avgTimeByOperator")}
                </Typography>
                <Box className={classes.chartBody}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data?.avgTimeByOperator ?? []}>
                      <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        stroke={chartTextColor}
                        fontSize={11}
                        interval={0}
                        angle={-20}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis stroke={chartTextColor} fontSize={12} />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={value => [
                          `${value} min`,
                          i18n.t("dashboard.metrics.avgTime"),
                        ]}
                      />
                      <Bar
                        dataKey="minutes"
                        name={i18n.t("dashboard.metrics.avgTime")}
                        fill="#eab308"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" className={classes.chartPaper}>
                <Typography variant="subtitle1" className={classes.chartTitle}>
                  {i18n.t("dashboard.charts.queueStats")}
                </Typography>
                <Box className={classes.chartBody}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data?.queueStats ?? []}>
                      <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="queue"
                        stroke={chartTextColor}
                        fontSize={11}
                        interval={0}
                        angle={-20}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis allowDecimals={false} stroke={chartTextColor} fontSize={12} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend />
                      <Bar
                        dataKey="open"
                        stackId="a"
                        name={i18n.t("dashboard.metrics.open")}
                        fill="#3b82f6"
                      />
                      <Bar
                        dataKey="pending"
                        stackId="a"
                        name={i18n.t("dashboard.metrics.pending")}
                        fill="#990000"
                      />
                      <Bar
                        dataKey="closed"
                        stackId="a"
                        name={i18n.t("dashboard.metrics.closed")}
                        fill="#22c55e"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" className={classes.chartTitle}>
                  {i18n.t("dashboard.charts.attendantsTable")}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{i18n.t("dashboard.table.attendant")}</TableCell>
                        <TableCell align="right">
                          {i18n.t("dashboard.table.tickets")}
                        </TableCell>
                        <TableCell align="right">
                          {i18n.t("dashboard.table.messages")}
                        </TableCell>
                        <TableCell align="right">
                          {i18n.t("dashboard.table.avgMessages")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.attendants ?? []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            {i18n.t("dashboard.table.empty")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.attendants.map(row => (
                          <TableRow key={row.name}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell align="right">{row.tickets}</TableCell>
                            <TableCell align="right">{row.messages}</TableCell>
                            <TableCell align="right">
                              {row.avgMessagesPerTicket}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default DashboardLS;
